import { useEffect, useRef, useState } from 'react';

const DEFAULT_OBSERVER_OPTIONS = {
  root: null,
  rootMargin: '0px 0px 500px 0px',
  threshold: 1.0,
};

/**
 * Lazy loads an image that is used with `background-image` css property (i.e. hero's on homepage and post-a-listing)
 *
 * @param {Image} lazyImage - Image src that should be lazy loaded
 * @param {Object} observerOptions - Options that will be passed to the IntersectionObserver object
 * @param {?htmlElement} observerOptions.root - element that is used as the viewport for checking visibility of the targe
 * @param {string} observerOptions.rootMargin - Margin around the root
 * @param {number} observerOptions.threshold - Indicates what percentage of the target's visibility the observer's callback should be executed
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API for more info on
 * `root`, `rootMargin`, and `threshold`
 *
 * @returns {[lazyBackgroundImageRef, lazyBackgroundImage]}
 *  - lazyBackgroundImageRef is the ref to be attached to the element containing the image to be lazy loaded
 *  - lazyBackgroundImage - the image that will be lazy loaded; should be placed in element src attribute
 *
 */
const useLazyBackgroundImage = (lazyImage: null | string = null, observerOptions = DEFAULT_OBSERVER_OPTIONS) => {
  const lazyBackgroundImageRef = useRef(null);
  const observer = useRef<IntersectionObserver | null>(null);
  const [lazyBackgroundImage, setLazyBackgroundImage] = useState<string>();

  useEffect(() => {
    observer.current = new IntersectionObserver(([entry]) => {
      if (entry.intersectionRatio === 1 && typeof lazyImage === 'string') {
        setLazyBackgroundImage(lazyImage);

        // Once loaded, unobserve the ref to prevent superfluous calls
        observer.current?.unobserve(entry.target);
      }
    }, observerOptions);

    if (lazyBackgroundImageRef.current) {
      observer.current.observe(lazyBackgroundImageRef.current);
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);

  return [lazyBackgroundImageRef, lazyBackgroundImage];
};

export default useLazyBackgroundImage;
